import React, { useState, useCallback, useRef, useEffect } from 'react';

// ─── Badge ─────────────────────────────────────────────────────────────────────
const BADGE_STYLES = {
  green:  { background:'rgba(22,163,74,0.1)',  color:'#16a34a', border:'1px solid rgba(22,163,74,0.2)' },
  red:    { background:'rgba(239,68,68,0.1)',  color:'#dc2626', border:'1px solid rgba(239,68,68,0.2)' },
  amber:  { background:'rgba(245,158,11,0.1)', color:'#d97706', border:'1px solid rgba(245,158,11,0.2)' },
  blue:   { background:'rgba(59,130,246,0.1)', color:'#2563eb', border:'1px solid rgba(59,130,246,0.2)' },
  navy:   { background:'rgba(28,25,96,0.08)',  color:'var(--navy)', border:'1px solid rgba(28,25,96,0.15)' },
};

export function Badge({ type='navy', children, style }) {
  const s = BADGE_STYLES[type] || BADGE_STYLES.navy;
  return (
    <span style={{ ...s, padding:'2px 10px', borderRadius:20, fontSize:11, fontWeight:600, whiteSpace:'nowrap', ...style }}>
      {children}
    </span>
  );
}

export function StatusBadge({ status }) {
  const map = { pending:'amber', approved:'green', rejected:'red', active:'green', signed:'blue', potential:'amber', closed:'red' };
  return <Badge type={map[status]||'navy'}>{status||'—'}</Badge>;
}

// ─── Spinner ──────────────────────────────────────────────────────────────────
export function Spinner() {
  return (
    <div style={{ display:'flex', justifyContent:'center', alignItems:'center', padding:60 }}>
      <div style={{ width:32, height:32, border:'3px solid var(--border)', borderTopColor:'var(--navy)', borderRadius:'50%', animation:'spin 0.7s linear infinite' }} />
    </div>
  );
}

// ─── Empty State ──────────────────────────────────────────────────────────────
export function EmptyState({ icon='ti-inbox', message='Nothing here yet', sub }) {
  return (
    <div style={{ padding:'40px 20px', textAlign:'center' }}>
      <i className={`ti ${icon}`} style={{ fontSize:36, color:'var(--border)', display:'block', marginBottom:10 }} />
      <div style={{ fontSize:14, fontWeight:500, color:'var(--text-tertiary)' }}>{message}</div>
      {sub && <div style={{ fontSize:12, color:'var(--text-tertiary)', marginTop:4 }}>{sub}</div>}
    </div>
  );
}

// ─── SearchInput ──────────────────────────────────────────────────────────────
export function SearchInput({ value, onChange, placeholder='Search...', style }) {
  return (
    <div style={{ position:'relative', ...style }}>
      <i className="ti ti-search" style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', color:'var(--text-tertiary)', fontSize:14, pointerEvents:'none' }} />
      <input
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        style={{ paddingLeft:32, width:'100%' }}
      />
    </div>
  );
}

// ─── SearchableSelect ──────────────────────────────────────────────────────────
// Drop-in replacement for a plain <select>. Pass `options` as an array of
// { value, label, sub? } objects, plus `value`/`onChange` like a normal select.
// Click outside or press Escape to close. Keyboard: type to filter, click a row to pick.
export function SearchableSelect({ value, onChange, options, placeholder='Select...', searchPlaceholder='Search...', style, disabled }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) { setOpen(false); setQuery(''); }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => { if (open && inputRef.current) inputRef.current.focus(); }, [open]);

  const selected = options.find(o => String(o.value) === String(value));
  const filtered = options.filter(o => {
    const q = query.toLowerCase();
    return !q || (o.label||'').toLowerCase().includes(q) || (o.sub||'').toLowerCase().includes(q);
  });

  return (
    <div ref={wrapRef} style={{ position:'relative', ...style }}>
      <div
        onClick={() => !disabled && setOpen(o => !o)}
        style={{
          display:'flex', alignItems:'center', justifyContent:'space-between', gap:8,
          padding:'8px 12px', border:'1px solid var(--border)', borderRadius:'var(--r-md)',
          background: disabled ? 'var(--bg)' : '#fff', cursor: disabled ? 'not-allowed' : 'pointer',
          fontSize:13, color: selected ? 'var(--text-primary)' : 'var(--text-tertiary)',
        }}>
        <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{selected ? selected.label : placeholder}</span>
        <i className={`ti ti-chevron-${open?'up':'down'}`} style={{ fontSize:13, color:'var(--text-tertiary)', flexShrink:0 }} />
      </div>
      {open && (
        <div style={{
          position:'absolute', top:'calc(100% + 4px)', left:0, right:0, zIndex:50,
          background:'#fff', border:'1px solid var(--border)', borderRadius:'var(--r-md)',
          boxShadow:'0 8px 24px rgba(0,0,0,0.12)', maxHeight:280, display:'flex', flexDirection:'column',
        }}>
          <div style={{ padding:8, borderBottom:'1px solid var(--border)' }}>
            <SearchInput value={query} onChange={setQuery} placeholder={searchPlaceholder} />
          </div>
          <div style={{ overflowY:'auto', maxHeight:228 }}>
            {filtered.length === 0 ? (
              <div style={{ padding:'14px 12px', fontSize:12, color:'var(--text-tertiary)', textAlign:'center' }}>No matches</div>
            ) : filtered.map(o => (
              <div key={o.value} onClick={() => { onChange(o.value); setOpen(false); setQuery(''); }}
                style={{ padding:'9px 12px', cursor:'pointer', background: String(o.value)===String(value)?'var(--blue-bg)':'transparent' }}
                onMouseEnter={e=>{ if(String(o.value)!==String(value)) e.currentTarget.style.background='var(--bg)'; }}
                onMouseLeave={e=>{ if(String(o.value)!==String(value)) e.currentTarget.style.background='transparent'; }}>
                <div style={{ fontSize:13, fontWeight: String(o.value)===String(value)?600:400, color: String(o.value)===String(value)?'var(--navy)':'var(--text-primary)' }}>
                  {o.label}{String(o.value)===String(value)?' ✓':''}
                </div>
                {o.sub && <div style={{ fontSize:11, color:'var(--text-tertiary)' }}>{o.sub}</div>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


export function useToast() {
  const [toast, setToast] = useState(null);
  const showToast = useCallback((msg, type='success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  }, []);
  const Toast = toast ? (
    <div style={{
      position:'fixed', bottom:24, right:24, zIndex:999,
      padding:'12px 20px', borderRadius:'var(--r-md)',
      background: toast.type==='error'?'var(--red)':'var(--navy)',
      color:'#fff', fontSize:13, fontWeight:500,
      boxShadow:'0 4px 20px rgba(0,0,0,0.15)',
      display:'flex', alignItems:'center', gap:8,
      animation:'slideInRight 0.2s ease',
    }}>
      <i className={`ti ${toast.type==='error'?'ti-alert-circle':'ti-check'}`} style={{ fontSize:16 }} />
      {toast.msg}
    </div>
  ) : null;
  return [showToast, Toast];
}

// ─── Modal ────────────────────────────────────────────────────────────────────
export function Modal({ title, onClose, children, footer, width=480 }) {
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.35)', zIndex:300, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }} onClick={onClose}>
      <div style={{ background:'#fff', borderRadius:'var(--r-lg)', width:'100%', maxWidth:width, maxHeight:'90vh', overflow:'auto', boxShadow:'0 20px 60px rgba(0,0,0,0.2)' }} onClick={e=>e.stopPropagation()}>
        <div style={{ padding:'16px 20px', borderBottom:'1px solid var(--border)', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div style={{ fontSize:15, fontWeight:600, color:'var(--navy)' }}>{title}</div>
          <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', fontSize:20, color:'var(--text-tertiary)', lineHeight:1, padding:'2px 6px' }}>✕</button>
        </div>
        <div style={{ padding:20 }}>{children}</div>
        {footer && <div style={{ padding:'12px 20px', borderTop:'1px solid var(--border)', display:'flex', justifyContent:'flex-end', gap:8 }}>{footer}</div>}
      </div>
    </div>
  );
}

// Panel with title/icon/action support
export function Panel({ children, style, title, icon, action }) {
  if (title) {
    return (
      <div className="card" style={{ overflow: 'hidden', ...style }}>
        <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {icon && <i className={`ti ${icon}`} style={{ fontSize: 15, color: 'var(--navy)' }} />}
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--navy)' }}>{title}</span>
          </div>
          {action}
        </div>
        {children}
      </div>
    );
  }
  return <div className="card" style={{ padding: 16, ...style }}>{children}</div>;
}

// MetricCard for dashboard stats
export function MetricCard({ label, value, sub, icon, color }) {
  return (
    <div className="card" style={{ padding: '14px 16px', display: 'flex', gap: 12, alignItems: 'center' }}>
      {icon && (
        <div style={{ width: 40, height: 40, borderRadius: 'var(--r-md)', background: 'var(--blue-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <i className={`ti ${icon}`} style={{ fontSize: 18, color: color || 'var(--navy)' }} />
        </div>
      )}
      <div>
        <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginBottom: 2 }}>{label}</div>
        <div style={{ fontSize: 22, fontWeight: 700, color: color || 'var(--navy)', lineHeight: 1.1 }}>{value}</div>
        {sub && <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 2 }}>{sub}</div>}
      </div>
    </div>
  );
}
